import { createAuth0Client } from "@auth0/auth0-spa-js";
import { ref } from "vue";
import { AUTH_ROUTES, AUTH_STORAGE_KEYS } from "../constants/auth.js";

const getEnv = (key) => {
  return process.env[`VUE_APP_${key}`];
};

let auth0ClientInstance = null;
let initPromise = null;

export const isAuth0Loading = ref(true);
export const auth0User = ref(null);
export const isAuth0Authenticated = ref(false);

/**
 * Returns the configured redirect URI for Auth0 callbacks.
 * Falls back to window.location.origin + /callback if not explicitly configured.
 */
export const getRedirectUri = () => {
  const configuredUri = getEnv("AUTH0_REDIRECT_URI");
  if (configuredUri) {
    return configuredUri;
  }
  if (typeof window !== "undefined") {
    return `${window.location.origin}${AUTH_ROUTES.CALLBACK}`;
  }
  return "";
};

/**
 * Initializes the Auth0 client singleton.
 * Prevents race conditions by caching and returning the in-flight initialization promise.
 */
export const initAuth0 = async () => {
  if (auth0ClientInstance) {
    return auth0ClientInstance;
  }

  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    isAuth0Loading.value = true;
    try {
      const domain = getEnv("AUTH0_DOMAIN") || "auth.projectblue.com";
      const clientId = getEnv("AUTH0_CLIENT_ID") || "ActXY3i5r9gMbPPT99TnOsrz5FuEpbfN";
      const organization = getEnv("AUTH0_ORGANIZATION") || "org_hxDieiWhXZUISHgY";
      const redirectUri = getRedirectUri();

      auth0ClientInstance = await createAuth0Client({
        domain,
        clientId,
        authorizationParams: {
          redirect_uri: redirectUri,
          organization: organization,
        },
        useRefreshTokens: true,
        cacheLocation: "localstorage",
      });

      const authed = await auth0ClientInstance.isAuthenticated();
      isAuth0Authenticated.value = authed;
      if (authed) {
        auth0User.value = await auth0ClientInstance.getUser();
      }

      return auth0ClientInstance;
    } catch (error) {
      console.error("Failed to initialize Auth0 client:", error);
      auth0ClientInstance = null;
      throw error;
    } finally {
      isAuth0Loading.value = false;
      initPromise = null;
    }
  })();

  return initPromise;
};

/**
 * Initiates Auth0 login redirect.
 * @param {Object} options - Optional authorization parameters (e.g. screen_hint, login_hint)
 */
export const loginWithAuth0 = async (options = {}) => {
  const client = await initAuth0();
  const organization = getEnv("AUTH0_ORGANIZATION") || "org_hxDieiWhXZUISHgY";
  const redirectUri = getRedirectUri();

  await client.loginWithRedirect({
    authorizationParams: {
      redirect_uri: redirectUri,
      organization: organization,
      prompt: "login",
      ...options,
    },
  });
};

/**
 * Logs out of Auth0 and purges local session storage.
 * @param {string} returnTo - URL to redirect to after logout
 */
export const logoutFromAuth0 = async (returnTo) => {
  const client = await initAuth0();
  
  if (typeof window !== "undefined") {
    localStorage.removeItem(AUTH_STORAGE_KEYS.JWT);
    localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(AUTH_STORAGE_KEYS.USER_EMAIL);
    localStorage.removeItem(AUTH_STORAGE_KEYS.INVITE_TOKEN);
    localStorage.removeItem(AUTH_STORAGE_KEYS.INVITE_DATA);
  }

  const defaultReturnTo = typeof window !== "undefined" 
    ? window.location.origin 
    : "https://projectblue.com";

  await client.logout({
    logoutParams: {
      returnTo: returnTo || defaultReturnTo,
    },
  });
};

/**
 * Handles Auth0 redirect callback, parses tokens, and returns user payload.
 */
export const handleAuth0Callback = async () => {
  const client = await initAuth0();
  const result = await client.handleRedirectCallback();

  isAuth0Authenticated.value = await client.isAuthenticated();
  if (isAuth0Authenticated.value) {
    auth0User.value = await client.getUser();
  }

  const idTokenClaims = await client.getIdTokenClaims();
  const idToken = idTokenClaims?.__raw || null;

  return {
    appState: result.appState,
    user: auth0User.value,
    idToken,
  };
};

/**
 * Silently retrieves a fresh token from Auth0.
 */
export const getAuth0TokenSilently = async () => {
  const client = await initAuth0();
  return await client.getTokenSilently();
};
